import type { FileManagerRef } from 'devextreme-react/file-manager';
import { when } from 'devextreme/core/utils/deferred';
import type { BrowseItem } from './fileBrowserApi';
import { searchItems } from './fileBrowserApi';

export const JARVIS_FILE_SEARCH = 'jarvisFileSearch';
export const SERVER_SEARCH_MIN_LENGTH = 2;

export type FileBrowserItemInfo = {
  fileItem?: {
    name?: string;
    isParentFolder?: boolean;
  };
};

type FileManagerItemView = {
  option: (name: string, value?: unknown) => unknown;
  refresh: () => PromiseLike<unknown>;
};

type FileManagerInternal = {
  element: () => HTMLElement;
  _itemView?: FileManagerItemView;
  _getItemViewItems?: () => unknown;
  _jarvisSearchPatched?: boolean;
  _jarvisGetSearchQuery?: () => string;
  _jarvisStableGetItems?: () => unknown;
};

export type FileBrowserSearchController = {
  getQuery: () => string;
  setQuery: (query: string) => void;
  clear: () => void;
  apply: () => void;
};

export type FileBrowserServerSearchContext = {
  mountId: string;
  relativePath: string;
};

export type FileBrowserSearchOptions = {
  getServerSearchContext?: () => FileBrowserServerSearchContext | null;
};

function shouldUseServerSearch(query: string): boolean {
  return query.trim().length >= SERVER_SEARCH_MIN_LENGTH;
}

export function filterItemInfosBySearch(
  items: readonly FileBrowserItemInfo[],
  query: string,
  serverHits?: ReadonlySet<string>
): FileBrowserItemInfo[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [...items];

  return items.filter((item) => {
    if (item.fileItem?.isParentFolder) return true;
    const name = item.fileItem?.name ?? '';
    if (name.toLowerCase().includes(trimmed)) return true;
    return serverHits?.has(name) ?? false;
  });
}

function highlightFileNameMatch(nameElement: Element, fileName: string, query: string): void {
  const lowerName = fileName.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerName.indexOf(lowerQuery);
  if (index < 0) return;

  const before = fileName.slice(0, index);
  const match = fileName.slice(index, index + query.length);
  const after = fileName.slice(index + query.length);

  nameElement.replaceChildren();
  if (before) nameElement.appendChild(document.createTextNode(before));

  const mark = document.createElement('span');
  mark.className = 'dx-datagrid-search-text';
  mark.textContent = match;
  nameElement.appendChild(mark);

  if (after) nameElement.appendChild(document.createTextNode(after));
}

function applySearchHighlights(root: HTMLElement, query: string): void {
  const trimmed = query.trim();
  if (!trimmed) return;

  const selectors = ['.dx-filemanager-details-item-name', '.dx-filemanager-thumbnails-item-name'];
  for (const selector of selectors) {
    for (const nameElement of root.querySelectorAll<HTMLElement>(selector)) {
      const fileName = nameElement.getAttribute('data-original-name') ?? nameElement.textContent ?? '';
      if (!nameElement.hasAttribute('data-original-name')) {
        nameElement.setAttribute('data-original-name', fileName);
      }
      highlightFileNameMatch(nameElement, fileName, trimmed);
    }
  }
}

function getFileManagerInternal(
  fileManagerRef: { current: FileManagerRef | null }
): FileManagerInternal | undefined {
  return fileManagerRef.current?.instance() as FileManagerInternal | undefined;
}

function ensureSearchDataPatch(
  fileManager: FileManagerInternal | undefined,
  getQuery: () => string,
  getServerHits: () => ReadonlySet<string> | undefined
): void {
  if (!fileManager) return;

  fileManager._jarvisGetSearchQuery = getQuery;
  if (fileManager._jarvisSearchPatched) return;

  const originalGetItems = fileManager._getItemViewItems?.bind(fileManager);
  if (!originalGetItems) return;

  fileManager._jarvisSearchPatched = true;
  fileManager._getItemViewItems = () =>
    when(originalGetItems()).then((items) =>
      filterItemInfosBySearch(
        items as FileBrowserItemInfo[],
        fileManager._jarvisGetSearchQuery?.() ?? '',
        getServerHits()
      )
    );
}

function ensureStableGetItems(fileManager: FileManagerInternal): () => unknown {
  if (!fileManager._jarvisStableGetItems) {
    fileManager._jarvisStableGetItems = () => fileManager._getItemViewItems?.();
  }
  return fileManager._jarvisStableGetItems;
}

function syncItemViewGetItems(fileManager: FileManagerInternal | undefined): void {
  const itemView = fileManager?._itemView;
  if (!itemView || !fileManager?._getItemViewItems) return;

  const stableGetter = ensureStableGetItems(fileManager);
  if (itemView.option('getItems') !== stableGetter) {
    itemView.option('getItems', stableGetter);
  }
}

function applyDomRowFilter(root: HTMLElement, query: string): void {
  const trimmed = query.trim().toLowerCase();

  for (const row of root.querySelectorAll<HTMLElement>('.dx-filemanager-details .dx-data-row')) {
    if (row.classList.contains('dx-filemanager-parent-directory-item')) {
      row.style.display = '';
      continue;
    }
    const nameElement = row.querySelector<HTMLElement>('.dx-filemanager-details-item-name');
    const fileName = nameElement?.textContent ?? '';
    const matches = !trimmed || fileName.toLowerCase().includes(trimmed);
    row.style.display = matches ? '' : 'none';
  }

  for (const item of root.querySelectorAll<HTMLElement>('.dx-filemanager-thumbnails-item')) {
    const nameElement = item.querySelector<HTMLElement>('.dx-filemanager-thumbnails-item-name');
    const fileName = nameElement?.textContent ?? '';
    const matches = !trimmed || fileName.toLowerCase().includes(trimmed);
    item.style.display = matches ? '' : 'none';
  }
}

function refreshFilteredItemView(
  fileManagerRef: { current: FileManagerRef | null },
  getQuery: () => string,
  getServerHits: () => ReadonlySet<string> | undefined
): void {
  const fileManager = getFileManagerInternal(fileManagerRef);
  const query = getQuery();
  ensureSearchDataPatch(fileManager, getQuery, getServerHits);
  syncItemViewGetItems(fileManager);

  const itemView = fileManager?._itemView;
  const root = fileManager?.element();

  if (!itemView || !fileManager?._jarvisSearchPatched) {
    if (root) {
      applyDomRowFilter(root, query);
      applySearchHighlights(root, query);
    }
    return;
  }

  void Promise.resolve(itemView.refresh()).then(() => {
    if (root) applySearchHighlights(root, query);
  });
}

export function createFileBrowserSearchController(
  fileManagerRef: { current: FileManagerRef | null },
  options?: FileBrowserSearchOptions
): FileBrowserSearchController {
  let query = '';
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let serverHits: ReadonlySet<string> | undefined;
  let serverRequestId = 0;

  const getQuery = () => query;
  const getServerHits = () => serverHits;

  const refreshServerHits = () => {
    const trimmed = query.trim();
    const context = options?.getServerSearchContext?.() ?? null;
    if (!shouldUseServerSearch(trimmed) || !context) {
      serverHits = undefined;
      return;
    }

    const requestId = ++serverRequestId;
    void searchItems(context.mountId, trimmed, context.relativePath)
      .then((items: BrowseItem[]) => {
        if (requestId !== serverRequestId) return;
        serverHits = new Set(items.map((item) => item.name));
        apply();
      })
      .catch(() => {
        if (requestId !== serverRequestId) return;
        serverHits = undefined;
      });
  };

  const apply = () => {
    refreshFilteredItemView(fileManagerRef, getQuery, getServerHits);
  };

  const scheduleApply = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      refreshServerHits();
      apply();
    }, 120);
  };

  return {
    getQuery: () => query,
    setQuery: (next) => {
      query = next;
      scheduleApply();
    },
    clear: () => {
      if (!query) return;
      query = '';
      serverHits = undefined;
      serverRequestId += 1;
      if (debounceTimer) clearTimeout(debounceTimer);
      apply();
    },
    apply
  };
}

export function clearFileBrowserSearchToolbarValue(
  fileManagerRef: { current: FileManagerRef | null }
): void {
  const fileManager = fileManagerRef.current?.instance();
  if (!fileManager) return;

  const items = fileManager.option('toolbar.items') as Array<{ name?: string }> | undefined;
  const index = items?.findIndex((item) => item.name === JARVIS_FILE_SEARCH) ?? -1;
  if (index >= 0) {
    fileManager.option(`toolbar.items[${index}].options.value`, '');
  }
}

export function clearFileBrowserSearch(
  fileManagerRef: { current: FileManagerRef | null },
  search: FileBrowserSearchController
): void {
  search.clear();
  clearFileBrowserSearchToolbarValue(fileManagerRef);
}

export function wireFileBrowserSearchInput(
  fileManagerRef: { current: FileManagerRef | null },
  search: FileBrowserSearchController
): void {
  const fileManager = fileManagerRef.current?.instance();
  const root = fileManager?.element();
  if (!root || root.getAttribute('data-jarvis-search-delegated') === '1') return;

  root.setAttribute('data-jarvis-search-delegated', '1');
  root.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.closest('.jarvis-file-browser-search')) return;
    search.setQuery(target.value);
  });
}

export function syncFileBrowserSearch(
  fileManagerRef: { current: FileManagerRef | null },
  search: FileBrowserSearchController,
  getServerHits?: () => ReadonlySet<string> | undefined
): void {
  const fileManager = getFileManagerInternal(fileManagerRef);
  ensureSearchDataPatch(fileManager, () => search.getQuery(), getServerHits ?? (() => undefined));
  syncItemViewGetItems(fileManager);
  wireFileBrowserSearchInput(fileManagerRef, search);
  if (search.getQuery()) {
    search.apply();
  }
}

export function buildFileBrowserSearchOptions(search: FileBrowserSearchController): {
  placeholder: string;
  width: number;
  valueChangeEvent: string;
  mode: 'search';
  showClearButton: boolean;
  onValueChanged: (e: { value?: string }) => void;
} {
  return {
    placeholder: 'Search files…',
    width: 220,
    valueChangeEvent: 'keyup input',
    mode: 'search',
    showClearButton: true,
    onValueChanged: (e) => search.setQuery(e.value ?? '')
  };
}
